import React from 'react';
import { useTranslation } from 'react-i18next';
import { TbLayoutBottombar, TbLayoutSidebarRight } from 'react-icons/tb';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import PropertyPanel from '../workflow/panels/PropertyPanel';
import IconWrapper from '../common/IconWrapper';
import { setDockPrefs, useDockPrefs } from './dockPrefs';
import { useSelector } from 'react-redux';
import { showToast } from '../common/Toast';

/**
 * 디자이너의 「노드 속성」 영역 — 머리(제목·위치 전환·접기)와 속성 패널.
 *
 * 예전에는 "데이터 목록"·"AI" 탭도 있었다. 데이터 목록은 헤더의 「데이터 정의」 창으로, AI 는 헤더의
 * 「AI 에이전트」 버튼이 여는 오른쪽 창(AiDrawer)으로 옮겼다 — 속성을 보면서 AI 와 이야기할 수 있게(현장 2026-09-22).
 */
const RightSidePanel: React.FC = () => {
  const { t } = useTranslation();
  const dock = useDockPrefs();
  const bottom = dock.position === 'bottom';
  // 고른 노드의 ID — 모든 노드(서브플로 포함)에 같은 자리. 캔버스 위에는 띄우지 않는다(현장 2026-10-01)
  const nodeId: string | null = useSelector((s: any) => {
    const id = s.workflow?.selectedNodeId;
    return id && (s.workflow?.nodes || []).some((n: any) => n.id === id) ? id : null;
  });
  const copyId = () => {
    if (!nodeId) return;
    navigator.clipboard?.writeText(nodeId).then(() => showToast(t('layout:side.idCopied', { id: nodeId })), () => {});
  };

  return (
    <div className="h-full flex flex-col min-h-0" data-point="panel:props">
      <div className="flex shrink-0 border-b border-gray-200 bg-gray-100">
        <div className="flex-1 min-w-0 px-3 py-2 text-sm font-medium text-gray-700 flex items-center gap-2">
          <span className="shrink-0">{t('layout:side.props')}</span>
          {nodeId && (
            <button type="button" onClick={copyId} title={t('layout:side.copyId')}
              className="min-w-0 truncate text-xs font-normal font-mono text-gray-500 hover:text-gray-800 px-1.5 py-0.5 rounded hover:bg-gray-200">
              ID: {nodeId}
            </button>
          )}
        </div>
        {/* 위치 전환(오른쪽 ↔ 아래)과 접기 — 캔버스를 넓게 쓰고 싶을 때 */}
        <div className="flex items-center gap-0.5 px-1 shrink-0">
          <button
            type="button"
            className="p-1.5 rounded text-gray-500 hover:text-gray-800 hover:bg-gray-200"
            title={bottom ? t('layout:dock.toRight') : t('layout:dock.toBottom')}
            onClick={() => setDockPrefs({ position: bottom ? 'right' : 'bottom' })}
          >
            <IconWrapper icon={bottom ? TbLayoutSidebarRight : TbLayoutBottombar} size={16} />
          </button>
          <button
            type="button"
            className="p-1.5 rounded text-gray-500 hover:text-gray-800 hover:bg-gray-200"
            title={t('layout:dock.collapse')}
            onClick={() => setDockPrefs({ collapsed: true })}
          >
            <IconWrapper icon={bottom ? FiChevronDown : FiChevronRight} size={16} />
          </button>
        </div>
      </div>
      <div className="prop-scroll flex-1 min-h-0 overflow-y-auto">
        <PropertyPanel />
      </div>
    </div>
  );
};

export default RightSidePanel;
